/**
 * Currency Context for handling multi-currency display
 * 
 * Backend stores all prices in USD, but frontend can display prices in
 * other currencies (e.g., CNY) using daily-updated exchange rates.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { shippingLabelEndpointPrefix } from '../config';

// Supported currencies
export type Currency = 'USD' | 'CNY';

interface ExchangeRates {
    USD_TO_CNY: number;
    USD_TO_EUR?: number;
    USD_TO_GBP?: number;
    last_updated: string | null;
}

interface CurrencyConfig {
    selectedCurrency: Currency;
    exchangeRates: ExchangeRates;
}

interface CurrencyContextType extends CurrencyConfig {
    setCurrency: (currency: Currency) => void;
    toggleCurrency: () => void;
    convertToSelectedCurrency: (usdAmount: number) => number;
    convertFromCurrency: (amount: number, fromCurrency: Currency) => number;
    formatPrice: (usdAmount: number, options?: { showOriginal?: boolean; decimals?: number }) => string;
    refreshRates: () => Promise<void>;
    isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Default fallback rates if API fails
const DEFAULT_RATES: ExchangeRates = {
    USD_TO_CNY: 7.20,
    USD_TO_EUR: 0.85,
    USD_TO_GBP: 0.74,
    last_updated: null
};

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Load saved currency preference from localStorage
    const [selectedCurrency, setSelectedCurrency] = useState<Currency>(() => {
        return (localStorage.getItem('preferred_currency') as Currency) || 'USD';
    });

    const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(DEFAULT_RATES);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch exchange rates from backend
    const fetchRates = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${shippingLabelEndpointPrefix}/api/labeling/customer/dropshipping/conversion`);
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data?.currency) {
                    setExchangeRates({
                        USD_TO_CNY: result.data.currency.USD_TO_CNY,
                        USD_TO_EUR: result.data.currency.USD_TO_EUR,
                        USD_TO_GBP: result.data.currency.USD_TO_GBP,
                        last_updated: result.data.currency.last_updated
                    });
                    console.log('✅ Exchange rates updated:', result.data.currency);
                }
            } else {
                console.warn('Failed to fetch exchange rates, using defaults');
            }
        } catch (error) {
            console.error('Error fetching currency rates:', error);
            // Keep using default rates on error
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch rates on mount and refresh daily
    useEffect(() => {
        fetchRates();
        
        // Refresh rates every 24 hours
        const interval = setInterval(fetchRates, 24 * 60 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [fetchRates]);

    const setCurrency = useCallback((currency: Currency) => {
        setSelectedCurrency(currency);
        localStorage.setItem('preferred_currency', currency);
        console.log(`Currency switched to: ${currency}`);
    }, []);

    const toggleCurrency = useCallback(() => {
        const newCurrency: Currency = selectedCurrency === 'USD' ? 'CNY' : 'USD';
        setCurrency(newCurrency);
    }, [selectedCurrency, setCurrency]);

    /**
     * Convert USD amount to selected currency
     */
    const convertToSelectedCurrency = useCallback((usdAmount: number): number => {
        if (selectedCurrency === 'USD') return usdAmount;
        
        // Convert USD to CNY
        if (selectedCurrency === 'CNY') {
            return usdAmount * exchangeRates.USD_TO_CNY;
        }
        
        return usdAmount;
    }, [selectedCurrency, exchangeRates]);

    /**
     * Convert from any currency to USD (backend canonical format)
     */
    const convertFromCurrency = useCallback((amount: number, fromCurrency: Currency): number => {
        if (fromCurrency === 'USD') return amount;
        
        // Convert CNY to USD
        if (fromCurrency === 'CNY') {
            return amount / exchangeRates.USD_TO_CNY;
        }
        
        return amount;
    }, [exchangeRates]);

    /**
     * Format price with currency symbol
     * 
     * @param usdAmount - Amount in USD (backend canonical format)
     * @param options.showOriginal - Show original USD amount for non-USD currencies
     * @param options.decimals - Number of decimal places (default: 2)
     */
    const formatPrice = useCallback((
        usdAmount: number, 
        options?: { showOriginal?: boolean; decimals?: number }
    ): string => {
        const { showOriginal = false, decimals = 2 } = options || {};
        
        const convertedAmount = convertToSelectedCurrency(usdAmount);
        const symbol = selectedCurrency === 'USD' ? '$' : '¥';
        const formattedPrice = `${symbol}${convertedAmount.toFixed(decimals)}`;
        
        // Optionally show original USD price for reference
        if (showOriginal && selectedCurrency !== 'USD') {
            return `${formattedPrice} (~$${usdAmount.toFixed(decimals)})`;
        }
        
        return formattedPrice;
    }, [selectedCurrency, convertToSelectedCurrency]);

    const refreshRates = useCallback(async () => {
        await fetchRates();
    }, [fetchRates]);

    return (
        <CurrencyContext.Provider value={{
            selectedCurrency,
            exchangeRates,
            setCurrency,
            toggleCurrency,
            convertToSelectedCurrency,
            convertFromCurrency,
            formatPrice,
            refreshRates,
            isLoading
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

/**
 * Hook to use currency context
 */
export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

// Default export for backward compatibility
export default useCurrency;
